/**
 * CspPage — CSP（升學及職涯起航平台）product landing page.
 *
 * CSP is YADA's school-branded platform for further-study and career
 * navigation: a no-code, one-stop portal (Studies Hub, FuturePath
 * Odyssey, Featured Stories, mock interviews, CV clinic, mentorship)
 * that a school can switch on under its own name and domain.
 *
 * WHY THIS PAGE IS NOT CMS-DRIVEN (unlike Harbour / ValuesIncubator)
 * -----------------------------------------------------------------
 * Those two read their body copy from `cms_product_pages` so an
 * operator can edit them in the admin without a deploy. CSP's copy is
 * a PRODUCT SHEET — a fixed set of feature cards, a stats row, an
 * offer banner and a set of marketing taglines that are reviewed as a
 * unit. Splitting it into a CMS row would let an operator change the
 * "first 50 schools" offer without the FAQ agreeing with it, so the
 * copy lives in i18n instead, versioned with the code that renders it.
 *
 * VISUAL IDENTITY
 * ---------------
 * The page carries its own stylesheet (src/styles/csp.css), scoped
 * under `.csp-root` so the cream / green / gold palette reaches this
 * page and nothing else. The site navbar and footer around it keep
 * the YADA identity, while the page body is the CSP product sheet.
 * This mirrors the way the standalone BiteLearn landing page carries
 * its own CSS.
 *
 * The stylesheet is imported from src/main.tsx, NOT from here: the
 * prerender pass (scripts/prerender.tsx) renders this component in
 * plain Node via tsx, and Node cannot import a `.css` file — a
 * component-level CSS import fails the whole prerender with
 * `Unknown file extension ".css"`. main.tsx is the browser entry, so
 * Vite bundles the stylesheet there and the prerendered pages inherit
 * it through the same <link> the SPA shell uses.
 *
 * IMAGES
 * ------
 * Every image is referenced by its PUBLIC path, never imported from
 * src/assets — the prerender pass renders in plain Node and cannot
 * resolve asset imports. Each one carries an explicit width/height
 * (CLS) and `loading="lazy"` below the fold; the hero image is eager
 * because it is above the fold.
 */
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { SITE } from '@/content/site'
import SEO from '@/utils/seo'
import { buildBreadcrumbList, buildFAQPage } from '@/utils/structuredData'
import { buildLocalePath, parseLocaleFromPath } from '@/utils/localePath'

/** The CSP platform's own site — an external destination, always a plain <a>. */
const CSP_SITE_URL = 'https://csp.yada.hk'

/** Public asset paths (see the IMAGES note above). */
const IMG_HERO = '/products/csp/hero.jpg'
const IMG_LOGO = '/products/csp/csp-logo.png'
const IMG_FUTUREPATH = '/products/csp/futurepath-insert.jpg'
const IMG_STORIES = '/products/csp/stories-insert.jpg'
const IMG_QR = '/products/csp/qr-whatsapp.png'

interface StatItem {
  value: string
  label: string
}

interface FeatureItem {
  title: string
  text: string
}

interface StepItem {
  title: string
  text: string
}

interface FaqItem {
  question: string
  answer: string
}

/**
 * asText — the missing-string guard.
 *
 * i18next echoes the KEY back when a translation is missing, so a
 * missing string would otherwise reach the page as a dotted key path
 * ("csp.landing.hero.badge"). A value that still looks like one of
 * our namespaces is therefore treated as missing and renders as an
 * empty string. Same pattern as `asText()` in ProductDetailPage.
 */
function asText(value: unknown): string {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('csp.landing.') || trimmed.startsWith('seo.')) return ''
  return value
}

/**
 * asList — the missing-list guard.
 *
 * With `returnObjects: true` a missing key comes back as the key
 * STRING, not an array, so anything that is not an array is treated
 * as an empty list rather than a crash in `.map()`.
 */
function asList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export default function CspPage() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const locale = parseLocaleFromPath(pathname)

  // ── Hero ──────────────────────────────────────────────────────────────
  const heroBadge = asText(t('csp.landing.hero.badge'))
  const heroTitle = asText(t('csp.landing.hero.title'))
  const heroTitleEn = asText(t('csp.landing.hero.titleEn'))
  const heroLede = asText(t('csp.landing.hero.lede'))
  const heroChips = asList<string>(t('csp.landing.hero.chips', { returnObjects: true }))
  const heroCtaWhatsapp = asText(t('csp.landing.hero.ctaWhatsapp'))
  const heroCtaSite = asText(t('csp.landing.hero.ctaSite'))
  const heroImageAlt = asText(t('csp.landing.hero.imageAlt'))
  const heroLogoAlt = asText(t('csp.landing.hero.logoAlt'))
  const heroBadgeTitle = asText(t('csp.landing.hero.badgeTitle'))
  const heroBadgeText = asText(t('csp.landing.hero.badgeText'))
  const heroWhatsappText = asText(t('csp.landing.hero.whatsappText'))

  // ── Stats ─────────────────────────────────────────────────────────────
  const statsLabel = asText(t('csp.landing.stats.label'))
  const statItems = asList<StatItem>(t('csp.landing.stats.items', { returnObjects: true }))

  // ── Problem ───────────────────────────────────────────────────────────
  const problemKicker = asText(t('csp.landing.problem.kicker'))
  const problemTitle = asText(t('csp.landing.problem.title'))
  const problemPainBefore = asText(t('csp.landing.problem.painBefore'))
  const problemPainStrong = asText(t('csp.landing.problem.painStrong'))
  const problemPainAfter = asText(t('csp.landing.problem.painAfter'))
  const problemPromiseHighlight = asText(t('csp.landing.problem.promiseHighlight'))
  const problemPromiseText = asText(t('csp.landing.problem.promiseText'))
  const problemImageAlt = asText(t('csp.landing.problem.imageAlt'))

  // ── Solution ──────────────────────────────────────────────────────────
  const solutionKicker = asText(t('csp.landing.solution.kicker'))
  const solutionTitle = asText(t('csp.landing.solution.title'))
  const solutionIntro = asText(t('csp.landing.solution.intro'))
  const solutionChecklist = asList<string>(
    t('csp.landing.solution.checklist', { returnObjects: true }),
  )

  // ── Features ──────────────────────────────────────────────────────────
  const featuresKicker = asText(t('csp.landing.features.kicker'))
  const featuresTitle = asText(t('csp.landing.features.title'))
  const featuresIntro = asText(t('csp.landing.features.intro'))
  const featureItems = asList<FeatureItem>(
    t('csp.landing.features.items', { returnObjects: true }),
  )

  // ── Callouts ──────────────────────────────────────────────────────────
  const futurepathName = asText(t('csp.landing.callouts.futurepath.name'))
  const futurepathTag = asText(t('csp.landing.callouts.futurepath.tag'))
  const futurepathText = asText(t('csp.landing.callouts.futurepath.text'))
  const futurepathImageAlt = asText(t('csp.landing.callouts.futurepath.imageAlt'))
  const storiesName = asText(t('csp.landing.callouts.stories.name'))
  const storiesTag = asText(t('csp.landing.callouts.stories.tag'))
  const storiesText = asText(t('csp.landing.callouts.stories.text'))
  const storiesImageAlt = asText(t('csp.landing.callouts.stories.imageAlt'))

  // ── How it starts ─────────────────────────────────────────────────────
  const startKicker = asText(t('csp.landing.start.kicker'))
  const startTitle = asText(t('csp.landing.start.title'))
  const startSteps = asList<StepItem>(t('csp.landing.start.steps', { returnObjects: true }))

  // ── Offer ─────────────────────────────────────────────────────────────
  const offerTag = asText(t('csp.landing.offer.tag'))
  const offerBadgeBig = asText(t('csp.landing.offer.badgeBig'))
  const offerBadgeSub = asText(t('csp.landing.offer.badgeSub'))
  const offerHeadline = asText(t('csp.landing.offer.headline'))
  const offerSubline = asText(t('csp.landing.offer.subline'))
  const offerCtaWhatsapp = asText(t('csp.landing.offer.ctaWhatsapp'))
  const offerCtaSite = asText(t('csp.landing.offer.ctaSite'))
  const offerWhatsappText = asText(t('csp.landing.offer.whatsappText'))

  // ── FAQ ───────────────────────────────────────────────────────────────
  const faqKicker = asText(t('csp.landing.faq.kicker'))
  const faqTitle = asText(t('csp.landing.faq.title'))
  const faqItems = asList<FaqItem>(t('csp.landing.faq.items', { returnObjects: true }))

  // ── Closing CTA ───────────────────────────────────────────────────────
  const closingTitle = asText(t('csp.landing.closing.title'))
  const closingText = asText(t('csp.landing.closing.text'))
  const closingCtaWhatsapp = asText(t('csp.landing.closing.ctaWhatsapp'))
  const closingCtaSite = asText(t('csp.landing.closing.ctaSite'))
  const closingWhatsappText = asText(t('csp.landing.closing.whatsappText'))
  const closingQrAlt = asText(t('csp.landing.closing.qrAlt'))

  // ── Breadcrumb ────────────────────────────────────────────────────────
  const crumbHome = asText(t('csp.landing.breadcrumb.home'))
  const crumbProducts = asText(t('csp.landing.breadcrumb.products'))
  const crumbSelf = asText(t('csp.landing.breadcrumb.self'))

  /**
   * WhatsApp deep link. The message text is per-locale (and differs
   * per CTA — the hero asks about the offer, the offer banner asks to
   * lock it in), so it is read from the bundle and URL-encoded here.
   */
  const whatsappHref = (message: string): string =>
    `${SITE.contact.whatsapp.link}?text=${encodeURIComponent(message)}`

  const faqSchema = faqItems
    .filter((item) => item.question && item.answer)
    .map((item) => ({ question: item.question, answer: item.answer }))

  return (
    <div className="csp-root csp-page">
      <SEO
        title={asText(t('seo.csp.title'))}
        description={asText(t('seo.csp.description'))}
        keywords={asText(t('seo.csp.keywords'))}
        jsonLd={[
          buildBreadcrumbList([
            { name: crumbHome, path: buildLocalePath('/', locale) },
            { name: crumbProducts, path: buildLocalePath('/products', locale) },
            { name: crumbSelf, path: buildLocalePath('/products/csp', locale) },
          ]),
          buildFAQPage(faqSchema),
        ]}
      />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="csp-hero" id="csp-top" aria-labelledby="csp-hero-title">
        <div className="csp-wrap csp-hero-grid">
          <div>
            <span className="csp-badge">
              <span className="csp-badge-dot" aria-hidden="true" />
              {heroBadge}
            </span>
            <h1 id="csp-hero-title">
              {heroTitle}
              <span className="csp-hero-en">{heroTitleEn}</span>
            </h1>
            <p className="csp-lede">{heroLede}</p>
            <div className="csp-chips">
              {heroChips.map((chip, index) => (
                <span
                  key={chip}
                  className={
                    index === heroChips.length - 1 ? 'csp-chip csp-chip-teal' : 'csp-chip'
                  }
                >
                  {chip}
                </span>
              ))}
            </div>
            <div className="csp-ctas">
              <a
                className="csp-btn csp-btn-primary"
                href={whatsappHref(heroWhatsappText)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {heroCtaWhatsapp}
              </a>
              <a
                className="csp-btn csp-btn-outline"
                href={CSP_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {heroCtaSite}
              </a>
            </div>
          </div>
          <div className="csp-hero-card">
            <span className="csp-sparkle csp-sparkle-tr" aria-hidden="true">
              ✦
            </span>
            <img
              src={IMG_HERO}
              alt={heroImageAlt}
              width={1280}
              height={720}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
            <div className="csp-float-badge">
              <img
                src={IMG_LOGO}
                alt={heroLogoAlt}
                width={352}
                height={352}
                loading="eager"
                decoding="async"
              />
              <div>
                <strong>{heroBadgeTitle}</strong>
                <span>{heroBadgeText}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────────────────────────── */}
      <div className="csp-wrap">
        <ul className="csp-stats" aria-label={statsLabel}>
          {statItems.map((item) => (
            <li className="csp-stat" key={item.value}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Problem / why now ─────────────────────────────────────────── */}
      <section className="csp-section" id="csp-problem" aria-labelledby="csp-problem-title">
        <div className="csp-wrap csp-split">
          <div>
            <span className="csp-kicker">{problemKicker}</span>
            <h2 className="csp-section-title" id="csp-problem-title">{problemTitle}</h2>
            <div className="csp-pain-box">
              {problemPainBefore}
              <strong>{problemPainStrong}</strong>
              {problemPainAfter}
            </div>
            <div className="csp-promise-box">
              <span className="csp-hl">{problemPromiseHighlight}</span> {problemPromiseText}
            </div>
          </div>
          <figure className="csp-panel">
            <img
              src={IMG_HERO}
              alt={problemImageAlt}
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
            />
          </figure>
        </div>
      </section>

      {/* ── Solution overview ──────────────────────────────────────────── */}
      <section className="csp-section" id="csp-solution" aria-labelledby="csp-solution-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{solutionKicker}</span>
          <h2 className="csp-section-title" id="csp-solution-title">{solutionTitle}</h2>
          <p className="csp-intro">{solutionIntro}</p>
          <ul className="csp-checklist">
            {solutionChecklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Feature modules ────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-features" aria-labelledby="csp-features-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{featuresKicker}</span>
          <h2 className="csp-section-title" id="csp-features-title">{featuresTitle}</h2>
          <p className="csp-intro">{featuresIntro}</p>
          <div className="csp-features">
            {featureItems.map((item, index) => (
              <article className="csp-feature" key={item.title}>
                <div className="csp-feature-n" aria-hidden="true">
                  {index + 1}
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>

          <div className="csp-callouts">
            <article className="csp-callout">
              <div className="csp-callout-head">
                <span className="csp-callout-name">{futurepathName}</span>
                <span className="csp-callout-tag">{futurepathTag}</span>
              </div>
              <div className="csp-callout-body">
                <img
                  src={IMG_FUTUREPATH}
                  alt={futurepathImageAlt}
                  width={1100}
                  height={520}
                  loading="lazy"
                  decoding="async"
                />
                <p>{futurepathText}</p>
              </div>
            </article>
            <article className="csp-callout">
              <div className="csp-callout-head csp-callout-head-teal">
                <span className="csp-callout-name">{storiesName}</span>
                <span className="csp-callout-tag">{storiesTag}</span>
              </div>
              <div className="csp-callout-body">
                <img
                  src={IMG_STORIES}
                  alt={storiesImageAlt}
                  width={1100}
                  height={640}
                  loading="lazy"
                  decoding="async"
                />
                <p>{storiesText}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ── How it starts ──────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-start" aria-labelledby="csp-start-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{startKicker}</span>
          <h2 className="csp-section-title" id="csp-start-title">{startTitle}</h2>
          <div className="csp-steps">
            {startSteps.map((item, index) => (
              <article className="csp-step" key={item.title}>
                <div className="csp-step-n" aria-hidden="true">
                  {index + 1}
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Offer band ─────────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-offer" aria-labelledby="csp-offer-title">
        <div className="csp-wrap">
          <div className="csp-offer">
            <div className="csp-offer-tag">{offerTag}</div>
            <div className="csp-offer-badge">
              <div className="csp-offer-big">{offerBadgeBig}</div>
              <div className="csp-offer-sub">{offerBadgeSub}</div>
            </div>
            <div>
              <h2 className="csp-offer-title" id="csp-offer-title">{offerHeadline}</h2>
              <div className="csp-offer-desc">{offerSubline}</div>
            </div>
            <div className="csp-offer-actions">
              <a
                className="csp-btn csp-btn-primary"
                href={whatsappHref(offerWhatsappText)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {offerCtaWhatsapp}
              </a>
              <a
                className="csp-btn csp-btn-outline"
                href={CSP_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {offerCtaSite}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-faq" aria-labelledby="csp-faq-title">
        <div className="csp-wrap">
          <span className="csp-kicker">{faqKicker}</span>
          <h2 className="csp-section-title" id="csp-faq-title">{faqTitle}</h2>
          <div className="csp-faq">
            {faqItems.map((item, index) => (
              <details className="csp-faq-item" key={item.question} open={index === 0}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ───────────────────────────────────────────────── */}
      <section className="csp-section" id="csp-cta" aria-labelledby="csp-cta-title">
        <div className="csp-wrap">
          <div className="csp-closing">
            <h2 id="csp-cta-title">{closingTitle}</h2>
            <p>{closingText}</p>
            <div className="csp-ctas">
              <a
                className="csp-btn csp-btn-gold"
                href={whatsappHref(closingWhatsappText)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {closingCtaWhatsapp}
              </a>
              <a
                className="csp-btn csp-btn-outline"
                href={CSP_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {closingCtaSite}
              </a>
            </div>
            <div className="csp-qr">
              <img
                src={IMG_QR}
                alt={closingQrAlt}
                width={348}
                height={348}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Back to products ──────────────────────────────────────────── */}
      <div className="back-to-products text-center mt-12">
        <Link to={buildLocalePath('/products', locale)} className="csp-back">
          <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
          <span>{asText(t('products.backToProducts'))}</span>
        </Link>
      </div>
    </div>
  )
}
